import React from "react";
import { Alert, Snackbar } from "@mui/material";

type ToastTone = "success" | "error" | "info";
interface ToastState {
  id: number;
  message: React.ReactNode;
  tone: ToastTone;
}

const ToastContext = React.createContext<(message: React.ReactNode, tone?: ToastTone) => void>(() => {});

/** One snackbar for the whole app; call useToast()("Saved"). */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = React.useState<ToastState | null>(null);
  const show = React.useCallback((message: React.ReactNode, tone: ToastTone = "success") => {
    setToast({ id: Date.now(), message, tone });
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <Snackbar
        key={toast?.id}
        open={!!toast}
        autoHideDuration={toast?.tone === "error" ? 8000 : 4000}
        onClose={(_, reason) => reason !== "clickaway" && setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: { xs: "calc(16px + env(safe-area-inset-bottom, 0px))" } }}
      >
        <Alert
          onClose={() => setToast(null)}
          severity={toast?.tone ?? "success"}
          variant="filled"
          sx={{ bgcolor: toast?.tone === "error" ? undefined : "#0E0E0E", color: "#fff", "& .MuiAlert-icon": { color: "#85E8AD" } }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export const useToast = () => React.useContext(ToastContext);
